import React from 'react'

import PropTypes from 'prop-types'
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd'
import { Box, IconButton, Tooltip, Typography } from '@material-ui/core'
import {
  Check as CheckIcon,
  Edit2 as EditIcon,
  Info as InfoIcon,
  Move as MoveIcon,
  Trash2 as TrashIcon,
  X as XIcon,
} from 'react-feather'
import clsx from 'clsx'

import ColorPicker from '../ColorPicker'
import useStyles from './styles'

import constants from 'constants/index'

const ColumnList = ({
  columns,
  editingId,
  editData,
  editNameRef,
  onStartEdit,
  onCancelEdit,
  onSaveEdit,
  onEditColorChange,
  onEditTextColorChange,
  onDeleteClick,
  onDragEnd,
  canManageColumns,
  canDestroyColumn,
}) => {
  const { KANBAN_COLUMN_ROLE_FINALIZED } = constants.activities

  const classes = useStyles()

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <Droppable droppableId="columns-list">
        {(provided) => (
          <Box ref={provided.innerRef} {...provided.droppableProps}>
            {columns.map((col, index) => (
              <Draggable
                key={col.id}
                draggableId={String(col.id)}
                index={index}
                isDragDisabled={
                  col.role === KANBAN_COLUMN_ROLE_FINALIZED || !canManageColumns
                }
              >
                {(provided, snapshot) => (
                  <Box
                    ref={provided.innerRef}
                    {...provided.draggableProps}
                    className={clsx(classes.row, {
                      [classes.rowDragging]: snapshot.isDragging,
                    })}
                  >
                    <Tooltip
                      title={
                        col.role === KANBAN_COLUMN_ROLE_FINALIZED
                          ? 'Coluna Finalizadas sempre fica por último'
                          : !canManageColumns
                          ? 'Sem permissão para reordenar colunas'
                          : ''
                      }
                    >
                      <Box
                        {...provided.dragHandleProps}
                        className={clsx(classes.dragHandle, {
                          [classes.dragHandleDisabled]:
                            col.role === KANBAN_COLUMN_ROLE_FINALIZED ||
                            !canManageColumns,
                        })}
                      >
                        <MoveIcon size={14} />
                      </Box>
                    </Tooltip>

                    <Typography className={classes.idCell}>
                      #{index + 1}
                    </Typography>

                    {editingId === col.id ? (
                      <Tooltip
                        title={
                          col.isDefault
                            ? 'O nome de colunas padrão não pode ser alterado'
                            : ''
                        }
                      >
                        <input
                          className={classes.nameInput}
                          defaultValue={editData.name}
                          ref={editNameRef}
                          disabled={col.isDefault}
                          autoFocus
                        />
                      </Tooltip>
                    ) : (
                      <Box className={classes.nameCellWrapper}>
                        <Tooltip title={col.title}>
                          <Typography className={classes.nameCell}>
                            {col.title}
                          </Typography>
                        </Tooltip>
                        {col.isDefault && (
                          <Typography className={classes.defaultBadge}>
                            padrão
                          </Typography>
                        )}
                        {col.role === KANBAN_COLUMN_ROLE_FINALIZED && (
                          <Tooltip title="Esta coluna sempre será a última">
                            <span className={classes.infoIconWrapperSpaced}>
                              <InfoIcon size={12} />
                            </span>
                          </Tooltip>
                        )}
                      </Box>
                    )}

                    {editingId === col.id ? (
                      <ColorPicker
                        value={editData.color}
                        onChange={onEditColorChange}
                      />
                    ) : (
                      <Box
                        className={classes.colorDot}
                        style={{ backgroundColor: col.color }}
                      />
                    )}

                    {editingId === col.id ? (
                      <ColorPicker
                        value={editData.textColor}
                        onChange={onEditTextColorChange}
                      />
                    ) : (
                      <Box
                        className={classes.colorDot}
                        style={{ backgroundColor: col.textColor || '#FFFFFF' }}
                      />
                    )}

                    <Box className={classes.actionsCell}>
                      {editingId === col.id ? (
                        <>
                          <Tooltip title="Salvar">
                            <IconButton
                              size="small"
                              className={classes.actionBtn}
                              onClick={() => onSaveEdit(col.id)}
                            >
                              <CheckIcon size={14} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Cancelar">
                            <IconButton
                              size="small"
                              className={classes.actionBtn}
                              onClick={onCancelEdit}
                            >
                              <XIcon size={14} />
                            </IconButton>
                          </Tooltip>
                        </>
                      ) : (
                        <>
                          {canManageColumns && (
                            <Tooltip title="Editar">
                              <IconButton
                                size="small"
                                className={classes.actionBtn}
                                onClick={() => onStartEdit(col)}
                              >
                                <EditIcon size={14} />
                              </IconButton>
                            </Tooltip>
                          )}
                          {canDestroyColumn && (
                            <Tooltip
                              title={
                                col.isDefault
                                  ? 'Colunas padrão não podem ser excluídas'
                                  : 'Excluir'
                              }
                            >
                              <span>
                                <IconButton
                                  size="small"
                                  className={classes.actionBtn}
                                  onClick={() => onDeleteClick(col.id)}
                                  disabled={col.isDefault}
                                >
                                  <TrashIcon size={14} />
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}
                        </>
                      )}
                    </Box>
                  </Box>
                )}
              </Draggable>
            ))}
            {provided.placeholder}
          </Box>
        )}
      </Droppable>
    </DragDropContext>
  )
}

ColumnList.propTypes = {
  columns: PropTypes.array.isRequired,
  editingId: PropTypes.number,
  editData: PropTypes.shape({
    name: PropTypes.string,
    color: PropTypes.string,
    textColor: PropTypes.string,
  }).isRequired,
  editNameRef: PropTypes.object.isRequired,
  onStartEdit: PropTypes.func.isRequired,
  onCancelEdit: PropTypes.func.isRequired,
  onSaveEdit: PropTypes.func.isRequired,
  onEditColorChange: PropTypes.func.isRequired,
  onEditTextColorChange: PropTypes.func.isRequired,
  onDeleteClick: PropTypes.func.isRequired,
  onDragEnd: PropTypes.func.isRequired,
  canManageColumns: PropTypes.bool,
  canDestroyColumn: PropTypes.bool,
}

export default ColumnList
